// Pharmacy store lookup for everything that calls
// inventory/pharmacy/prescribe/search/.
//
// Stock is held per store, so a prescribe-search without `location_id` reports
// availability that belongs to no particular counter — the Out of Stock badge
// and the substitute list are both meaningless until the search is scoped to
// one pharmacy store. This module supplies that store.
//
// Org scoping is resolved server-side from the ORG-CODE header (same rule as
// orderSetsApi.js), so no tenant_id / hospital_id is ever sent from here.

import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

// API: GET inventory/stores/?is_pharmacy_store=true&is_active=true
// Only pharmacy stores can dispense, and a deactivated one must never be
// selectable — filtering server-side keeps both rules in one place.
const PHARMACY_STORE_PARAMS = {
  is_pharmacy_store: true,
  is_active: true,
  limit: 50,
  offset: 0,
};

function authHeaders() {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
  };
}

function errorMessage(err) {
  const data = err?.response?.data;
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.detail?.message === "string") return data.detail.message;
  if (typeof data?.message === "string") return data.message;
  return err?.message || "Could not load pharmacy stores.";
}

/** Backend row → { id, name, code }. `id` is what goes out as `location_id`. */
export function normalizePharmacyStore(row) {
  const rawId = row?.store_id ?? row?.id ?? row?.location_id;
  if (rawId === null || rawId === undefined || rawId === "") return null;
  const code = row?.store_code || row?.code || "";
  return {
    id: String(rawId),
    name: row?.store_name || row?.name || code || `Store ${rawId}`,
    code,
  };
}

// The list is org-level and doesn't change mid-session, while the Care Plan
// modal, the Order Management drawer and the Order Sets admin panel each want
// it — so it's fetched once per page load and shared. A failure clears the
// cache so the next screen retries instead of inheriting the error forever.
let storesPromise = null;

export async function fetchPharmacyStores({ force = false } = {}) {
  if (force) storesPromise = null;
  if (!storesPromise) {
    storesPromise = axios
      .get(`${BASE}inventory/stores/`, {
        headers: authHeaders(),
        params: PHARMACY_STORE_PARAMS,
      })
      .then((res) => {
        const payload = res?.data?.data ?? res?.data ?? [];
        const rows = Array.isArray(payload)
          ? payload
          : payload.results || payload.items || payload.data || [];
        return rows.map(normalizePharmacyStore).filter(Boolean);
      })
      .catch((err) => {
        storesPromise = null;
        throw new Error(errorMessage(err));
      });
  }
  return storesPromise;
}
